import { FC, ReactElement, ReactNode, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Stack, Typography } from '@mui/material';

import { getSubmitDisabled } from '@mopla/utils';

import {
	DRIVER_ANDROID_LINK,
	DRIVER_IOS_LINK,
	MplButton,
	StoreBadges,
} from '../../';
import { PasswordInput } from '../../atoms/PasswordInput/PasswordInput';
import { FormTitle } from '../../layouts/WelcomeScreen';
import { CompleteAnim } from '../../molecules/Animations';
import { DropdownIcon } from '../../molecules/DropdownIcon/DropdownIcon';
import { PasswordValidationConditions } from '../../validation/PasswordValidationConditions';

import {
	BrowserTitle,
	ErrorWrapper,
	LoginBox,
} from './ChangePasswordForm.styles';
import { ChangePasswordValidationSchema } from './ChangePasswordValidation';

interface IFormInput {
	password: string;
	confirmPassword: string;
}

interface ChangePasswordProps {
	showSuccess: boolean;
	onSuccessCallback: () => void;
	onSubmitCallback: () => void;
	onErrorCallback: () => void;
	isOnboarding?: boolean;
	driverApp?: boolean;
	verifyPasswordResetCode: any;
	confirmPasswordReset: any;
	getAuth: any;
	driverLoginForm?: ReactNode;
	render: (p: { content: ReactNode; submit: ReactNode }) => ReactElement;
}

export const ChangePasswordForm: FC<ChangePasswordProps> = ({
	onSuccessCallback,
	showSuccess,
	onSubmitCallback,
	onErrorCallback,
	isOnboarding,
	driverApp,
	verifyPasswordResetCode,
	confirmPasswordReset,
	getAuth,
	driverLoginForm,
	render,
}) => {
	const { t } = useTranslation([
		'changePassword',
		'validation',
		'registration',
	]);
	const [error, setError] = useState(false);
	const [showLogin, setShowLogin] = useState(false);
	const location = useLocation();

	const methods = useForm<IFormInput>({
		defaultValues: {
			password: '',
			confirmPassword: '',
		},
		resolver: yupResolver(ChangePasswordValidationSchema),
		mode: 'onTouched',
	});
	const {
		formState: { errors, touchedFields },
		handleSubmit,
		watch,
	} = methods;

	const passwordValue = watch('password');

	const onSubmit = (data: IFormInput) => {
		if (error) {
			onErrorCallback();
		}
		const { password } = data;
		const auth = getAuth();
		const actionCode = new URLSearchParams(location.search).get('oobCode');
		verifyPasswordResetCode(auth, actionCode as string)
			.then(() => {
				confirmPasswordReset(auth, actionCode as string, password)
					.then(() => {
						setError(false);
						onSuccessCallback();
						methods.reset({
							password: '',
						});
					})
					.catch(() => {
						setError(true);
					});
			})
			.catch(() => {
				setError(true);
			});
	};

	if (showSuccess) {
		if (driverApp && !Capacitor.isNativePlatform()) {
			return render({
				content: (
					<Stack className="content" gap={3}>
						<CompleteAnim loop />
						<FormTitle>
							{t(
								isOnboarding
									? 'success_screen.text.description_driver'
									: 'success_screen.title'
							)}
						</FormTitle>
						<Stack gap={2}>
							<Typography variant="body2" color="#66758F" textAlign="center">
								{t('success_screen.text.description_driver')}
							</Typography>
							<BrowserTitle>{t('text.easier')}</BrowserTitle>
							<Typography variant="body2" mb={2}>
								{t('text.download_app')}
							</Typography>
							<StoreBadges
								iosLink={DRIVER_IOS_LINK}
								androidLink={DRIVER_ANDROID_LINK}
							/>
							{driverLoginForm && (
								<>
									<LoginBox onClick={() => setShowLogin((p) => !p)}>
										<span>{t('text.login_in_browser')}</span>
										<DropdownIcon isOpened={showLogin} />
									</LoginBox>
									{showLogin && driverLoginForm}
								</>
							)}
						</Stack>
					</Stack>
				),
				submit: null,
			});
		}

		return render({
			content: (
				<Stack
					className="content"
					gap={3}
					data-testid="change-passwordForm-success-screen"
				>
					<CompleteAnim loop />
					<FormTitle>
						{t(
							isOnboarding
								? 'success_screen.text.description_driver'
								: 'success_screen.title'
						)}
					</FormTitle>
					<Typography variant="body2" color="#66758F" textAlign="center">
						{t('success_screen.text.description')}
					</Typography>
				</Stack>
			),
			submit: (
				<MplButton
					fullWidth
					color={driverApp ? 'secondary' : 'primaryButtonV2'}
					data-testid="successScreen_button_toLogin"
					onClick={onSubmitCallback}
				>
					{t('success_screen.button.to_the_login')}
				</MplButton>
			),
		});
	}

	const getButtonName = () => {
		if (error) return t('success_screen.button.to_login');
		return t('button.change_password');
	};

	return render({
		content: (
			<Stack className="content">
				<FormTitle>
					{t(isOnboarding ? 'text.title_driver' : 'text.title')}
				</FormTitle>
				<Box display="flex" justifyContent="center" sx={{ mb: 3 }}>
					<Typography variant="b2" color="solutions.600">
						{t('text.description')}
					</Typography>
				</Box>
				<FormProvider {...methods}>
					<form
						id="changePasswordForm"
						onSubmit={handleSubmit((data) => onSubmit(data))}
					>
						<Stack gap={2}>
							<Controller
								name="password"
								control={methods.control}
								render={({ field }) => (
									<PasswordInput
										{...field}
										label={t('label.password')}
										error={Boolean(
											errors.password?.message === 'password.no_spaces'
										)}
										helperText={
											errors.password?.message === 'password.no_spaces' &&
											t(`validation:${errors.password.message}`)
										}
										inputProps={{
											'data-testid': 'change-passwordForm-password-input',
										}}
										autoComplete="new-password"
									/>
								)}
							/>
							<ErrorWrapper error={error}>{t('text.expired')}</ErrorWrapper>
							<PasswordValidationConditions
								driverApp={driverApp}
								password={passwordValue}
								fieldTouched={touchedFields.password}
								checkPasswordErrors={Boolean(
									touchedFields.password && errors.password
								)}
							/>
							<Controller
								name="confirmPassword"
								control={methods.control}
								render={({ field }) => (
									<PasswordInput
										{...field}
										label={t('label.confirmPassword')}
										helperText={
											Boolean(errors.confirmPassword?.message) &&
											t(`validation:${errors.confirmPassword?.message}`)
										}
										error={Boolean(errors.confirmPassword?.message || error)}
										inputProps={{
											'data-testid':
												'change-passwordForm-confirmPassword-input',
										}}
										autoComplete="new-password"
									/>
								)}
							/>
						</Stack>
					</form>
				</FormProvider>
			</Stack>
		),
		submit: (
			<MplButton
				fullWidth
				color={driverApp ? 'secondary' : 'primaryButtonV2'}
				data-testid="forgot-password-button"
				disabled={getSubmitDisabled(methods, { whenInternallyInvalid: true })}
				type="submit"
				form="changePasswordForm"
			>
				{getButtonName()}
			</MplButton>
		),
	});
};
